/* ============================================================
   AMARESTA — THE STUDIO  ·  hs-studio.js
   Vanilla ES6, no dependencies. The whole customer flow:

     photo  → client-side crop (3:4 / 4:3) + downscale re-encode
              (max 2048 long edge, JPEG q0.9 — also converts HEIC)
              + consent → POST studio-upload
     style  → 11 locked styles → POST studio-preview
     preview→ poll GET studio-status every 2.5s (90s cap, honest
              painterly microcopy, NEVER a fake progress %)
     size   → POST studio-approve → live variants from the store's
              AJAX product endpoints → POST /cart/add.js with
              {_studio, Style, _preview_id} properties

   Honesty rules enforced here: no invented progress, watermark
   caveat at the reveal, screen-vs-print caveat at reveal + size,
   AI disclosure before purchase, Bauhaus "inspired by your photo"
   wording carried through. Every error state has a retry or a way
   back — the UI never dead-ends. Deep-linkable via location.hash.
   ============================================================ */
(() => {
  'use strict';

  /* ---------- 0 · config & feature gate ---------- */
  const CFG = window.HS_STUDIO || {};
  if (!CFG.fnBase) return;                       // flag off → placeholder page is showing
  const FN = String(CFG.fnBase).replace(/\/+$/, '');

  const POLL_MS       = 2500;                    // studio-status poll interval
  const POLL_CAP_MS   = 90000;                   // then a friendly "keep waiting?" prompt
  const GEN_GAP_MS    = 8000;                    // backend enforces ≥8s between gens
  const MIN_EDGE      = 500;                     // print-quality floor (source px, short edge)
  const MAX_LONG_EDGE = 2048;                    // upload re-encode target
  const JPEG_Q        = 0.9;
  const SKEY          = 'hsStudioV1';
  const STEPS         = ['photo', 'style', 'preview', 'size'];

  /* ---------- 1 · DOM ---------- */
  const hs = (name) => document.querySelector('[data-hs="' + name + '"]');
  const el = {
    rail: hs('rail'), whisper: hs('whisper'), sr: hs('sr'),
    easel: hs('easel'), invite: hs('invite'), file: hs('file'),
    cropbox: hs('cropbox'), cropimg: hs('cropimg'), photoimg: hs('photoimg'),
    paint: hs('paint'), paintline: hs('paintline'),
    revealimg: hs('revealimg'), easelnote: hs('easelnote'),
    panelPhoto: hs('panel-photo'), panelStyle: hs('panel-style'),
    panelPreview: hs('panel-preview'), panelSize: hs('panel-size'),
    restorednote: hs('restorednote'), seg: hs('seg'),
    orientP: hs('orient-portrait'), orientL: hs('orient-landscape'),
    consentwrap: hs('consentwrap'), consent: hs('consent'),
    msgPhoto: hs('msg-photo'), msgStyle: hs('msg-style'),
    msgPreview: hs('msg-preview'), msgSize: hs('msg-size'),
    btnChoose: hs('btn-choose'), btnUse: hs('btn-use'),
    btnRechoose: hs('btn-rechoose'), btnToStyles: hs('btn-tostyles'),
    stylegrid: hs('stylegrid'), btnNewPhoto: hs('btn-newphoto'),
    actReady: hs('act-ready'), actFail: hs('act-fail'), actWait: hs('act-wait'),
    btnLove: hs('btn-love'), btnAnother: hs('btn-another'),
    btnRetry: hs('btn-retry'), btnDifferent: hs('btn-different'),
    btnKeepWaiting: hs('btn-keepwaiting'), btnOtherStyle: hs('btn-otherstyle'),
    sizeui: hs('sizeui'), sizegroups: hs('sizegroups'), disclose: hs('disclose'),
    btnAdd: hs('btn-add'), btnBackPreview: hs('btn-backpreview'),
    confirm: hs('confirm'), confirmMeta: hs('confirm-meta'), btnAgain: hs('btn-again'),
    versions: hs('versions'), vstrip: hs('vstrip')
  };
  if (!el.easel || !el.stylegrid) return;        // template mismatch — do nothing rather than break

  /* ---------- 2 · copy (all honest; no timing or progress claims) ---------- */
  const COPY = {
    busy:       'The studio is busy right now — try again in a bit.',
    offline:    'We couldn’t reach the studio — check your connection and try again.',
    generic:    'Something went wrong on our end — please try again.',
    tooSmall:   'This photo is a bit small to print beautifully — got a larger one? Anything over 500 pixels on its short side works.',
    unreadable: 'We couldn’t read that photo — a JPG, PNG or a picture straight from your camera roll works best.',
    cartFail:   'We couldn’t add it to your cart — please try again.',
    genFail:    'That preview didn’t come out — it happens sometimes. Paint it again, or pick a different style.',
    genFail2:   'Two misses in a row — this style may not suit this photo. A different style usually does the trick.',
    stalled:    'Still painting — this one’s taking longer than usual. You can keep waiting, or try another style.',
    expired:    'That preview’s link has expired — want us to paint it again?',
    oneAtATime: 'One preview at a time — yours is still painting.',
    breather:   'Give the easel a few seconds between previews, then tap a style again.',
    pickSize:   'Pick a size first — then it’s cart time.',
    sizesFail:  'We couldn’t load sizes and prices — please try again.',
    watermark:  'Free preview — watermark comes off the real thing. Screen colors can differ slightly from print.',
    watermarkShort: 'Free preview — the watermark comes off the real thing.',
    inspiredBy: 'This one is an original composition inspired by your photo — not a likeness.',
    cropHint:   'Drag to position — pinch or scroll to zoom. This exact crop is what we print.'
  };
  // Chanel restraint: no whisper on the empty easel — its own script
  // invitation ("your photo begins here") carries that moment alone.
  const WHISPER = {
    empty: '', crop: 'make it yours…',
    saved: 'your photo is in…', style: 'pick its style…',
    painting: 'your art, in progress…', reveal: 'here it is…',
    size: 'make it real…'
  };

  /* ---------- 3 · state ---------- */
  // persisted (sessionStorage) — a refresh never loses work
  const state = {
    sessionId: null, uploadId: null, orientation: 'portrait',
    thumb: null,                 // small dataURL of the crop, for easel continuity
    previews: [],                // {id, styleId, styleName, likeness, status, url, token, stalled, failMsg}
    currentId: null
  };
  let step = 'photo';
  // volatile (rebuilt per page load)
  const vol = {
    img: null, imgURL: null,     // the decoded source photo
    crop: null,                  // {fw,fh,base,s,maxS,ox,oy}
    polls: new Map(),            // preview_id -> {started, timer, misses}
    lastGenAt: 0, failStreak: 0, genInFlight: false,
    products: null,              // [canvasProduct, posterProduct] from /products/*.js
    hashLock: false,
    paintTimer: 0, paintIdx: 0
  };
  const STYLES = (() => {
    try { return JSON.parse(document.getElementById('hs-styles-data').textContent); }
    catch (e) { return []; }
  })();
  const styleById = (id) => STYLES.find((s) => s.id === id) || null;
  const cur = () => state.previews.find((p) => p.id === state.currentId) || null;
  // stalled previews don't count: a hung backend job must never lock the style grid
  const anyPainting = () => state.previews.some((p) => p.status === 'painting' && !p.stalled);
  // approval tokens expire server-side (14 days); treat anything within an hour
  // of expiry as stale so a resumed old session re-approves instead of carting
  // a token that fulfillment would reject after payment
  const tokenFresh = (p) => !!(p && p.token && p.tokenExpires &&
    Date.parse(p.tokenExpires) - Date.now() > 3600 * 1000);

  function save() {
    try {
      sessionStorage.setItem(SKEY, JSON.stringify({
        v: 1, sessionId: state.sessionId, uploadId: state.uploadId,
        orientation: state.orientation, thumb: state.thumb,
        previews: state.previews, currentId: state.currentId
      }));
    } catch (e) { /* storage full/blocked — the flow still works, it just won't survive refresh */ }
  }
  function load() {
    try {
      const d = JSON.parse(sessionStorage.getItem(SKEY) || 'null');
      if (!d || d.v !== 1) return;
      state.sessionId = d.sessionId || null;
      state.uploadId = d.uploadId || null;
      state.orientation = d.orientation === 'landscape' ? 'landscape' : 'portrait';
      state.thumb = d.thumb || null;
      state.previews = Array.isArray(d.previews) ? d.previews : [];
      state.currentId = d.currentId || null;
    } catch (e) { /* corrupt entry — start clean */ }
  }

  /* ---------- 4 · backend + messaging helpers ---------- */
  async function api(path, opts) {
    let res;
    try { res = await fetch(FN + '/' + path, opts || {}); }
    catch (e) { const err = new Error(COPY.offline); err.network = true; throw err; }
    let data = null;
    try { data = await res.json(); } catch (e) { /* non-JSON body */ }
    if (!res.ok) {
      // backend errors arrive as {error:{code,message}} — our own friendly copy
      const err = new Error((data && data.error && data.error.message) || '');
      err.status = res.status;
      err.code = data && data.error && data.error.code;
      throw err;
    }
    return data || {};
  }
  function friendly(err) {
    if (!err) return COPY.generic;
    // backend-authored copy wins even on 429s (it says "come back tomorrow"
    // for daily limits — more honest than a generic "busy")
    if ((err.status === 429 || err.code === 'rate_limited' || err.code === 'cap_tripped') && !err.message) return COPY.busy;
    if (err.network) return COPY.offline;
    if (err.message) return err.message;      // backend-authored friendly copy
    return COPY.generic;
  }
  // kind: 'err' | 'ok' | 'note'; extra = optional element (e.g. a retry button)
  function setMsg(slot, text, kind, extra) {
    if (!slot) return;
    slot.textContent = '';
    if (!text) return;
    const p = document.createElement('p');
    p.className = kind === 'err' ? 'formerr' : (kind === 'ok' ? 'formok' : 'studio-note');
    p.setAttribute('role', kind === 'err' ? 'alert' : 'status');
    p.textContent = text;
    slot.appendChild(p);
    if (extra) slot.appendChild(extra);
  }
  const clearMsg = (slot) => setMsg(slot, '');
  function makeBtn(label, cls, onClick) {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'btn ' + cls; b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }
  function busy(btn, label) {
    if (!btn) return;
    if (!btn.dataset.label) btn.dataset.label = btn.textContent;
    btn.textContent = label; btn.disabled = true;
  }
  function unbusy(btn) {
    if (!btn || !btn.dataset.label) return;
    btn.textContent = btn.dataset.label; delete btn.dataset.label; btn.disabled = false;
  }
  const announce = (t) => { if (el.sr) el.sr.textContent = t; };
  const fmtMoney = (cents) => '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');

  /* ---------- 5 · steps, hash deep-links, master render ---------- */
  function reachable(s) {
    const p = cur();
    if (s === 'photo') return true;
    if (s === 'style') return !!state.uploadId;
    if (s === 'preview') return !!p;
    if (s === 'size') return !!(p && p.token && p.status === 'ready');
    return false;
  }
  function goStep(next, fromHash) {
    if (!STEPS.includes(next) || !reachable(next)) return;
    step = next;
    if (!fromHash && location.hash !== '#' + step) {
      vol.hashLock = true;
      location.hash = step;
      setTimeout(() => { vol.hashLock = false; }, 0);
    }
    if (step === 'size') buildSize();            // fetch/refresh live prices on entry
    render();
  }
  window.addEventListener('hashchange', () => {
    if (vol.hashLock) return;
    const s = (location.hash || '').replace('#', '');
    if (STEPS.includes(s) && reachable(s)) goStep(s, true);
    else if (location.hash !== '#' + step) {     // unreachable deep-link → snap back
      vol.hashLock = true;
      location.hash = step;
      setTimeout(() => { vol.hashLock = false; }, 0);
    }
  });

  function easelState() {
    const p = cur();
    if (step === 'photo') return vol.img ? 'crop' : (state.thumb ? 'saved' : 'empty');
    if (step === 'style') return state.thumb ? 'saved' : 'empty';
    if (step === 'preview') {
      if (p && p.status === 'ready' && p.url) return 'reveal';
      if (p && p.status === 'painting') return 'painting';
      return state.thumb ? 'saved' : 'empty';
    }
    if (step === 'size') return (p && p.url) ? 'reveal' : 'saved';
    return 'empty';
  }

  // animateReveal: true only when a preview has just finished — the one motion moment
  function render(animateReveal) {
    const p = cur();
    const es = easelState();

    // --- easel ---
    const prevEs = el.easel.getAttribute('data-state');
    el.easel.setAttribute('data-state', es);
    el.easel.classList.toggle('easel--land', state.orientation === 'landscape');
    if ((es === 'saved' || es === 'painting') && state.thumb && el.photoimg.src !== state.thumb) {
      el.photoimg.src = state.thumb;
    }
    if (es === 'reveal' && p && p.url) {
      if (el.revealimg.src !== p.url) el.revealimg.src = p.url;
      el.revealimg.alt = 'Your ' + p.styleName + ' preview — watermarked';
      if (animateReveal || prevEs !== 'reveal') {
        el.revealimg.classList.remove('in');
        void el.revealimg.offsetWidth;           // restart the CSS animation
        el.revealimg.classList.add('in');
      }
    }

    // --- whisper + note under the easel ---
    const wKey = (step === 'style') ? 'style' : (step === 'size') ? 'size' : es;
    el.whisper.textContent = WHISPER[wKey] || WHISPER.empty;
    if (es === 'crop') el.easelnote.textContent = COPY.cropHint;
    else if (es === 'reveal' && p) {
      // step 4 already carries the screen-vs-print caveat in .sizecaveat —
      // don't say it twice on one screen
      const wm = (step === 'size') ? COPY.watermarkShort : COPY.watermark;
      el.easelnote.textContent = (p.likeness === 'inspired-by')
        ? COPY.inspiredBy + ' ' + wm
        : wm;
    } else el.easelnote.textContent = '';

    // --- painting microcopy loop ---
    if (es === 'painting') startPaintLoop(p); else stopPaintLoop();

    // --- rail ---
    el.rail.querySelectorAll('button').forEach((b) => {
      const s = b.dataset.step;
      b.disabled = !reachable(s);
      if (s === step) b.setAttribute('aria-current', 'step');
      else b.removeAttribute('aria-current');
      b.classList.toggle('done', STEPS.indexOf(s) < STEPS.indexOf(step) && reachable(s));
    });

    // --- panels ---
    el.panelPhoto.hidden = step !== 'photo';
    el.panelStyle.hidden = step !== 'style';
    el.panelPreview.hidden = step !== 'preview';
    el.panelSize.hidden = step !== 'size';

    if (step === 'photo') {
      const hasImg = !!vol.img;
      el.seg.hidden = !hasImg;
      el.consentwrap.hidden = !hasImg;
      el.btnUse.hidden = !hasImg;
      el.btnUse.disabled = !(hasImg && el.consent.checked);
      el.btnRechoose.hidden = !hasImg;
      el.btnChoose.hidden = hasImg;
      el.restorednote.hidden = !(!hasImg && state.uploadId);
      el.btnToStyles.hidden = !(!hasImg && state.uploadId);
      el.orientP.setAttribute('aria-pressed', state.orientation === 'portrait' ? 'true' : 'false');
      el.orientL.setAttribute('aria-pressed', state.orientation === 'landscape' ? 'true' : 'false');
    }

    if (step === 'style') {
      const lock = anyPainting();
      el.stylegrid.querySelectorAll('.stylecard').forEach((c) => {
        c.setAttribute('aria-disabled', lock ? 'true' : 'false');
      });
      if (lock) setMsg(el.msgStyle, COPY.oneAtATime, 'note'); else clearMsg(el.msgStyle);
    }

    if (step === 'preview') {
      el.actReady.hidden = !(p && p.status === 'ready');
      el.actFail.hidden = !(p && p.status === 'failed');
      el.actWait.hidden = !(p && p.status === 'painting' && p.stalled);
      if (p && p.status === 'failed') {
        setMsg(el.msgPreview, p.failMsg || (vol.failStreak >= 2 ? COPY.genFail2 : COPY.genFail), 'err');
      } else if (p && p.status === 'painting' && p.stalled) {
        setMsg(el.msgPreview, COPY.stalled, 'note');
      } else clearMsg(el.msgPreview);
    }

    renderVersions();
  }

  /* ---------- 6 · painting microcopy — rotating, honest, style-aware ---------- */
  function paintLines(p) {
    return [
      'Mixing paint…',
      'Laying in the background…',
      (p && p.likeness === 'inspired-by') ? 'Arranging the shapes…' : 'Working on the likeness…',
      'Catching the light…',
      'Painting your preview…'
    ];
  }
  function startPaintLoop(p) {
    if (vol.paintTimer) return;
    const lines = paintLines(p);
    vol.paintIdx = 0;
    el.paintline.classList.remove('fade');
    el.paintline.textContent = lines[0];
    vol.paintTimer = setInterval(() => {
      el.paintline.classList.add('fade');
      setTimeout(() => {
        vol.paintIdx = (vol.paintIdx + 1) % lines.length;
        el.paintline.textContent = lines[vol.paintIdx];
        el.paintline.classList.remove('fade');
      }, 450);
    }, 3400);
  }
  function stopPaintLoop() {
    if (!vol.paintTimer) return;
    clearInterval(vol.paintTimer); vol.paintTimer = 0;
  }

  /* ---------- 7 · step 1 · photo: decode, guard, crop, re-encode, upload ---------- */
  const loadImage = (url) => new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('decode'));
    i.src = url;
  });

  async function handleFile(file) {
    clearMsg(el.msgPhoto);
    if (!file) return;
    const okType = /^image\//.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
    if (!okType) { setMsg(el.msgPhoto, COPY.unreadable, 'err'); return; }
    const url = URL.createObjectURL(file);
    let img;
    try { img = await loadImage(url); }
    catch (e) { URL.revokeObjectURL(url); setMsg(el.msgPhoto, COPY.unreadable, 'err'); return; }
    if (Math.min(img.naturalWidth, img.naturalHeight) < MIN_EDGE) {
      URL.revokeObjectURL(url);
      setMsg(el.msgPhoto, COPY.tooSmall, 'err');
      return;
    }
    if (vol.imgURL) URL.revokeObjectURL(vol.imgURL);
    vol.img = img; vol.imgURL = url; vol.crop = null;
    el.consent.checked = false;    // each photo needs its own rights attestation
    el.cropimg.src = url;
    if (step !== 'photo') goStep('photo'); else render();
    initCrop(0);   // reading clientWidth forces layout, so this works synchronously
  }

  /* --- crop engine: cover-fit + drag + pinch + wheel, clamped so the
         frame is always fully covered; zoom capped so the crop region
         never drops below the 500px print-quality floor --- */
  function initCrop(tries) {
    if (!vol.img) return;
    const fw = el.cropbox.clientWidth, fh = el.cropbox.clientHeight;
    // not laid out yet (shouldn't happen, but never brick the flow on it) → retry;
    // setTimeout rather than rAF so it also works in backgrounded/non-painting tabs
    if (!fw || !fh) { if (tries < 10) setTimeout(() => initCrop(tries + 1), 50); return; }
    const nw = vol.img.naturalWidth, nh = vol.img.naturalHeight;
    const base = Math.max(fw / nw, fh / nh);
    vol.crop = {
      fw, fh, base, s: 1,
      maxS: Math.max(1, Math.min(4, Math.min(fw, fh) / (MIN_EDGE * base))),
      ox: (fw - nw * base) / 2, oy: (fh - nh * base) / 2
    };
    el.cropimg.style.width = (nw * base) + 'px';
    el.cropimg.style.height = (nh * base) + 'px';
    applyCrop();
  }
  function clampCrop() {
    const c = vol.crop;
    const w = vol.img.naturalWidth * c.base * c.s, h = vol.img.naturalHeight * c.base * c.s;
    c.ox = Math.min(0, Math.max(c.fw - w, c.ox));
    c.oy = Math.min(0, Math.max(c.fh - h, c.oy));
  }
  function applyCrop() {
    const c = vol.crop;
    clampCrop();
    el.cropimg.style.transform = 'translate(' + c.ox + 'px,' + c.oy + 'px) scale(' + c.s + ')';
  }
  function zoomTo(newS, px, py) {
    const c = vol.crop;
    newS = Math.max(1, Math.min(c.maxS, newS));
    const ratio = newS / c.s;
    c.ox = px - (px - c.ox) * ratio;
    c.oy = py - (py - c.oy) * ratio;
    c.s = newS;
    applyCrop();
  }
  function relayoutCrop() {                      // frame resized / orientation flipped
    if (!vol.img || !vol.crop) { initCrop(0); return; }
    const c = vol.crop;
    const t = c.base * c.s;
    const cx = (c.fw / 2 - c.ox) / t, cy = (c.fh / 2 - c.oy) / t;   // source px at frame center
    const fw = el.cropbox.clientWidth, fh = el.cropbox.clientHeight;
    if (!fw || !fh) { setTimeout(relayoutCrop, 50); return; }
    const nw = vol.img.naturalWidth, nh = vol.img.naturalHeight;
    c.fw = fw; c.fh = fh;
    c.base = Math.max(fw / nw, fh / nh);
    c.maxS = Math.max(1, Math.min(4, Math.min(fw, fh) / (MIN_EDGE * c.base)));
    c.s = Math.max(1, Math.min(c.maxS, c.s));
    const t2 = c.base * c.s;
    c.ox = fw / 2 - cx * t2; c.oy = fh / 2 - cy * t2;
    el.cropimg.style.width = (nw * c.base) + 'px';
    el.cropimg.style.height = (nh * c.base) + 'px';
    applyCrop();
  }

  (function bindCrop() {
    const box = el.cropbox;
    const pts = new Map();
    let lastMid = null, lastDist = 0, rect = null;
    box.addEventListener('pointerdown', (e) => {
      if (!vol.crop) return;
      e.preventDefault();
      box.setPointerCapture(e.pointerId);
      rect = box.getBoundingClientRect();
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      lastDist = 0; lastMid = null;
    });
    box.addEventListener('pointermove', (e) => {
      if (!vol.crop || !pts.has(e.pointerId)) return;
      const prev = pts.get(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const c = vol.crop;
      if (pts.size === 1) {                      // one finger / mouse: pan
        c.ox += e.clientX - prev.x;
        c.oy += e.clientY - prev.y;
        applyCrop();
      } else {                                   // two fingers: pinch-zoom + pan
        const arr = [...pts.values()];
        const mid = { x: (arr[0].x + arr[1].x) / 2 - rect.left, y: (arr[0].y + arr[1].y) / 2 - rect.top };
        const dist = Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y);
        if (lastDist) {
          zoomTo(c.s * (dist / lastDist), mid.x, mid.y);
          c.ox += mid.x - lastMid.x; c.oy += mid.y - lastMid.y;
          applyCrop();
        }
        lastDist = dist; lastMid = mid;
      }
    });
    const lift = (e) => { pts.delete(e.pointerId); if (pts.size < 2) { lastDist = 0; lastMid = null; } };
    box.addEventListener('pointerup', lift);
    box.addEventListener('pointercancel', lift);
    box.addEventListener('wheel', (e) => {
      if (!vol.crop) return;
      e.preventDefault();
      const r = box.getBoundingClientRect();
      zoomTo(vol.crop.s * Math.exp(-e.deltaY * 0.0016), e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });
    // keyboard alternative (WCAG 2.1.1): arrows pan, +/- zoom, shift = bigger steps
    box.tabIndex = 0;
    box.setAttribute('role', 'application');
    box.setAttribute('aria-label', 'Position your photo: arrow keys move it, plus and minus zoom, hold Shift for bigger steps');
    box.addEventListener('keydown', (e) => {
      if (!vol.crop) return;
      const c = vol.crop;
      const stepPx = e.shiftKey ? 72 : 18;
      let handled = true;
      if (e.key === 'ArrowLeft') c.ox += stepPx;
      else if (e.key === 'ArrowRight') c.ox -= stepPx;
      else if (e.key === 'ArrowUp') c.oy += stepPx;
      else if (e.key === 'ArrowDown') c.oy -= stepPx;
      else if (e.key === '+' || e.key === '=') zoomTo(c.s * (e.shiftKey ? 1.2 : 1.08), c.fw / 2, c.fh / 2);
      else if (e.key === '-' || e.key === '_') zoomTo(c.s / (e.shiftKey ? 1.2 : 1.08), c.fw / 2, c.fh / 2);
      else handled = false;
      if (handled) { e.preventDefault(); applyCrop(); }
    });
  })();

  // render the current crop to a canvas; longTarget caps the long edge
  function renderCrop(longTarget) {
    const c = vol.crop, img = vol.img;
    const t = c.base * c.s;
    let sx = -c.ox / t, sy = -c.oy / t, sw = c.fw / t, sh = c.fh / t;
    sx = Math.max(0, Math.min(img.naturalWidth - 1, sx));
    sy = Math.max(0, Math.min(img.naturalHeight - 1, sy));
    sw = Math.min(img.naturalWidth - sx, sw);
    sh = Math.min(img.naturalHeight - sy, sh);
    const portrait = state.orientation === 'portrait';
    const outLong = Math.max(4, Math.min(longTarget, Math.round(Math.max(sw, sh))));
    const outW = portrait ? Math.round(outLong * 3 / 4) : outLong;
    const outH = portrait ? outLong : Math.round(outLong * 3 / 4);
    const cv = document.createElement('canvas');
    cv.width = outW; cv.height = outH;
    const ctx = cv.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
    return cv;
  }
  const canvasBlob = (cv) => new Promise((res, rej) =>
    cv.toBlob((b) => (b ? res(b) : rej(new Error('encode'))), 'image/jpeg', JPEG_Q));

  async function useThisPhoto() {
    if (!vol.img || !vol.crop || !el.consent.checked) return;
    busy(el.btnUse, 'Preparing your photo…');
    try {
      const blob = await canvasBlob(renderCrop(MAX_LONG_EDGE));   // downscale + JPEG (converts HEIC too)
      state.thumb = renderCrop(360).toDataURL('image/jpeg', 0.72);
      el.btnUse.textContent = 'Sending…';
      const fd = new FormData();
      fd.append('image', blob, 'photo.jpg');   // backend reads "image" (falls back to "file")
      fd.append('consent', 'true');
      if (state.sessionId) fd.append('session_id', state.sessionId);
      const data = await api('studio-upload', { method: 'POST', body: fd });
      state.sessionId = data.session_id || state.sessionId;
      state.uploadId = data.upload_id;
      save();
      clearMsg(el.msgPhoto);
      announce('Photo saved. Pick a style.');
      goStep('style');
    } catch (err) {
      setMsg(el.msgPhoto, friendly(err), 'err');
    } finally {
      unbusy(el.btnUse);
      render();
    }
  }

  function setOrientation(o) {
    if (state.orientation === o) return;
    state.orientation = o;
    save();
    render();                      // applies/removes .easel--land
    if (vol.img) relayoutCrop();   // then re-fit the photo to the new frame
  }
  function newPhoto() {
    if (vol.imgURL) URL.revokeObjectURL(vol.imgURL);
    vol.img = null; vol.imgURL = null; vol.crop = null;
    state.uploadId = null; state.thumb = null;    // previews stay revisitable in the strip
    el.consent.checked = false;
    save();
    goStep('photo');
    el.file.click();
  }

  /* ---------- 8 · step 2 · style cards + preview kickoff ---------- */
  function buildStyleCards() {
    el.stylegrid.textContent = '';
    STYLES.forEach((s) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'style stylecard' + (s.land ? ' stylecard--land' : '');
      b.setAttribute('aria-disabled', 'false');
      if (s.likeness === 'inspired-by') {
        const flag = document.createElement('span');
        flag.className = 'flag';
        flag.textContent = 'Inspired by your photo';
        b.appendChild(flag);
      }
      const img = document.createElement('img');
      img.src = s.thumb; img.alt = s.name + ' — style sample';
      img.loading = 'lazy'; img.decoding = 'async';
      b.appendChild(img);
      const name = document.createElement('div');
      name.className = 'name';
      name.textContent = s.name;
      const small = document.createElement('small');
      small.textContent = s.one_liner;
      name.appendChild(small);
      b.appendChild(name);
      b.addEventListener('click', () => pickStyle(s));
      el.stylegrid.appendChild(b);
    });
  }

  async function pickStyle(s) {
    if (!state.uploadId) { goStep('photo'); return; }
    if (vol.genInFlight) return;                 // double-tap guard: aria-disabled doesn't block clicks
    if (anyPainting()) { setMsg(el.msgStyle, COPY.oneAtATime, 'note'); return; }
    if (Date.now() - vol.lastGenAt < GEN_GAP_MS) { setMsg(el.msgStyle, COPY.breather, 'note'); return; }
    clearMsg(el.msgStyle);
    vol.genInFlight = true;
    el.stylegrid.querySelectorAll('.stylecard').forEach((c) => c.setAttribute('aria-disabled', 'true'));
    try {
      const data = await api('studio-preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: state.sessionId, upload_id: state.uploadId, style_id: s.id })
      });
      vol.lastGenAt = Date.now();
      const p = {
        id: data.preview_id, styleId: s.id, styleName: s.name,
        likeness: s.likeness, status: 'painting', url: null, token: null,
        stalled: false, failMsg: null
      };
      state.previews.push(p);
      state.currentId = p.id;
      save();
      announce('Painting your ' + s.name + ' preview.');
      goStep('preview');
      startPoll(p);
    } catch (err) {
      el.stylegrid.querySelectorAll('.stylecard').forEach((c) => c.setAttribute('aria-disabled', 'false'));
      setMsg(el.msgStyle, friendly(err), 'err');
    } finally {
      vol.genInFlight = false;
    }
  }

  /* ---------- 9 · step 3 · poll studio-status → the reveal ---------- */
  function stopPoll(pid) {
    const rec = vol.polls.get(pid);
    if (rec && rec.timer) clearTimeout(rec.timer);
    vol.polls.delete(pid);
  }
  function startPoll(p) {
    stopPoll(p.id);
    const rec = { started: Date.now(), timer: 0, misses: 0 };
    vol.polls.set(p.id, rec);
    const isShowing = () => step === 'preview' && state.currentId === p.id;
    const loop = async () => {
      if (!vol.polls.has(p.id)) return;
      if (Date.now() - rec.started > POLL_CAP_MS) {          // friendly cap — no silent hang
        p.stalled = true; save(); stopPoll(p.id);
        render();     // unconditional: the style grid's lock state depends on this too
        return;
      }
      let data = null;
      try {
        data = await api('studio-status?preview_id=' + encodeURIComponent(p.id));
        rec.misses = 0;
      } catch (err) {
        rec.misses += 1;
        if (rec.misses >= 4) {                               // repeated hard errors → honest failure
          p.status = 'failed'; p.failMsg = friendly(err); save();
          stopPoll(p.id); vol.failStreak += 1;
          render();   // unconditional: whatever step is showing must drop its painting lock
          return;
        }
      }
      if (data) {
        const st = String(data.status || '').toLowerCase();
        if (data.preview_url) {                              // done — the reveal
          p.status = 'ready'; p.url = data.preview_url; p.stalled = false; p.failMsg = null;
          vol.failStreak = 0; save(); stopPoll(p.id);
          announce('Your ' + p.styleName + ' preview is ready.');
          render(isShowing());
          return;
        }
        if (st === 'failed' || st === 'error' || st === 'blocked' || st === 'moderation_blocked' || st === 'moderated_out') {
          p.status = 'failed';
          // terminal payloads carry backend copy at top-level `message`
          // (moderated_out especially); error.message is the legacy shape
          p.failMsg = (typeof data.message === 'string' && data.message) ||
                      (data.error && data.error.message) || null;
          save(); stopPoll(p.id); vol.failStreak += 1;
          render();   // unconditional: whatever step is showing must drop its painting lock
          return;
        }
      }
      rec.timer = setTimeout(loop, POLL_MS);
    };
    loop();
  }
  function keepWaiting() {                                   // restart the 90s clock
    const p = cur();
    if (!p || p.status !== 'painting') return;
    p.stalled = false; save();
    startPoll(p);
    render();
  }
  function retrySameStyle() {                                // re-generate the same style
    const p = cur();
    const s = p && styleById(p.styleId);
    if (!s) { goStep('style'); return; }
    vol.lastGenAt = 0;                                       // deliberate user retry
    goStep('style');
    pickStyle(s);
  }

  // preview URLs are short-TTL signed — refresh once via studio-status if one expires
  async function refreshPreviewUrl(p) {
    try {
      const d = await api('studio-status?preview_id=' + encodeURIComponent(p.id));
      if (d.preview_url) { p.url = d.preview_url; save(); return true; }
    } catch (e) { /* fall through */ }
    return false;
  }
  el.revealimg.addEventListener('error', async () => {
    const p = cur();
    if (!p || !p.url || el.revealimg.dataset.retried === p.id) return;
    el.revealimg.dataset.retried = p.id;
    if (await refreshPreviewUrl(p)) { el.revealimg.src = p.url; return; }
    setMsg(el.msgPreview, COPY.expired, 'err', makeBtn('Paint it again', 'cta', retrySameStyle));
  });

  /* ---------- 10 · step 4 · approve, live sizes, add to cart ---------- */
  async function loveIt() {
    const p = cur();
    if (!p || p.status !== 'ready') return;
    if (tokenFresh(p)) { goStep('size'); return; }   // stale/expired → re-approve (backend renews)
    busy(el.btnLove, 'One moment…');
    try {
      const data = await api('studio-approve', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview_id: p.id })
      });
      p.token = data.token;
      p.tokenExpires = data.expires_at || null;
      save();
      clearMsg(el.msgPreview);
      goStep('size');
    } catch (err) {
      setMsg(el.msgPreview, friendly(err), 'err');
    } finally {
      unbusy(el.btnLove);
    }
  }

  function discloseText(p) {
    return (p && p.likeness === 'inspired-by')
      ? 'Made with AI — an original composition inspired by your photo. What you approved is exactly what we print. '
      : 'Made with AI from your photo. What you approved is exactly what we print. ';
  }

  async function buildSize() {
    const p = cur();
    if (!p) return;
    el.confirm.hidden = true;
    el.sizeui.hidden = false;
    // AI disclosure — pre-purchase, unmissable; Bauhaus keeps its inspired-by wording
    el.disclose.textContent = discloseText(p);
    const a = document.createElement('a');
    a.href = '/pages/refund-guarantee';
    a.textContent = 'Love-it guarantee';
    el.disclose.appendChild(a);
    el.disclose.appendChild(document.createTextNode('.'));

    if (!vol.products) {
      setMsg(el.msgSize, 'Fetching sizes and prices…', 'note');
      try {
        const get = (h) => fetch('/products/' + h + '.js', { headers: { Accept: 'application/json' } })
          .then((r) => { if (!r.ok) throw new Error(''); return r.json(); });
        vol.products = await Promise.all([
          get(CFG.canvasHandle || 'custom-canvas'),
          get(CFG.posterHandle || 'custom-poster')
        ]);
      } catch (err) {
        vol.products = null;
        setMsg(el.msgSize, COPY.sizesFail, 'err', makeBtn('Try again', 'ghost', buildSize));
        return;
      }
    }
    clearMsg(el.msgSize);
    renderSizeGroups();
  }

  function renderSizeGroups() {
    el.sizegroups.textContent = '';
    let first = null;
    vol.products.forEach((prod) => {
      const g = document.createElement('div');
      g.className = 'sizegroup';
      const h = document.createElement('h3');
      h.textContent = prod.title;                            // the store's own product name
      g.appendChild(h);
      (prod.variants || []).forEach((v) => {
        const label = document.createElement('label');
        label.className = 'sizeopt' + (v.available ? '' : ' soldout');
        const input = document.createElement('input');
        input.type = 'radio'; input.name = 'hs-size'; input.value = String(v.id);
        input.dataset.label = prod.title + ' · ' + v.title;
        input.dataset.price = String(v.price);
        input.disabled = !v.available;
        const t = document.createElement('span');
        t.className = 't'; t.textContent = v.title;
        const price = document.createElement('span');
        price.className = 'p';
        price.textContent = v.available ? fmtMoney(v.price) : 'Unavailable';   // live price, never hard-coded
        label.appendChild(input); label.appendChild(t); label.appendChild(price);
        input.addEventListener('change', () => {
          el.sizegroups.querySelectorAll('.sizeopt').forEach((o) => o.classList.remove('checked'));
          label.classList.add('checked');
          clearMsg(el.msgSize);
        });
        g.appendChild(label);
        if (!first && v.available) first = input;
      });
      el.sizegroups.appendChild(g);
    });
    if (first) {                                             // sensible default: first available size
      first.checked = true;
      first.closest('.sizeopt').classList.add('checked');
    }
  }

  async function addToCart() {
    const p = cur();
    if (!p || !p.token) { goStep('preview'); return; }
    if (!vol.products) { buildSize(); return; }    // sizes never loaded — re-fetch, don't hint at a picker that isn't there
    const sel = el.sizegroups.querySelector('input[name="hs-size"]:checked');
    if (!sel) { setMsg(el.msgSize, COPY.pickSize, 'err'); return; }
    busy(el.btnAdd, 'Adding…');
    try {
      if (!tokenFresh(p)) {                        // resumed old session: renew before money touches it
        try {
          const data = await api('studio-approve', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ preview_id: p.id })
          });
          p.token = data.token;
          p.tokenExpires = data.expires_at || null;
          save();
        } catch (err) {
          setMsg(el.msgSize, COPY.expired, 'err', makeBtn('Paint it again', 'cta', retrySameStyle));
          return;
        }
      }
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(sel.value),
          quantity: 1,
          properties: { _studio: p.token, Style: p.styleName, _preview_id: p.id }
        })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        // Shopify's description field is customer-facing copy; anything else → our copy
        setMsg(el.msgSize, (data && data.description) || COPY.cartFail, 'err');
        return;
      }
      el.sizeui.hidden = true;
      el.confirm.hidden = false;
      // price stays visible at the commitment moment — no surprises at checkout
      el.confirmMeta.textContent = sel.dataset.label + ' · ' + p.styleName
        + (sel.dataset.price ? ' — ' + fmtMoney(Number(sel.dataset.price)) : '');
      announce('Added to cart.');
    } catch (err) {
      setMsg(el.msgSize, COPY.cartFail, 'err');
    } finally {
      unbusy(el.btnAdd);
    }
  }

  /* ---------- 11 · "your versions" strip ---------- */
  function renderVersions() {
    const ready = state.previews.filter((p) => p.status === 'ready' && p.url);
    el.versions.hidden = ready.length === 0;
    if (!ready.length) return;
    el.vstrip.textContent = '';
    ready.forEach((p) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'v-item';
      if (p.id === state.currentId) b.setAttribute('aria-current', 'true');
      const img = document.createElement('img');
      img.src = p.url; img.alt = p.styleName + ' preview';
      img.loading = 'lazy'; img.decoding = 'async';
      img.addEventListener('error', async () => {
        if (img.dataset.retried) return;
        img.dataset.retried = '1';
        if (await refreshPreviewUrl(p)) img.src = p.url;
      });
      const span = document.createElement('span');
      span.textContent = p.styleName;
      b.appendChild(img); b.appendChild(span);
      b.addEventListener('click', () => {
        state.currentId = p.id;
        save();
        goStep('preview');
        render(true);                                        // replay the reveal — it earns it
      });
      el.vstrip.appendChild(b);
    });
  }

  /* ---------- 12 · events ---------- */
  el.invite.addEventListener('click', () => el.file.click());
  el.btnChoose.addEventListener('click', () => el.file.click());
  el.btnRechoose.addEventListener('click', () => el.file.click());
  el.file.addEventListener('change', () => {
    const f = el.file.files && el.file.files[0];
    el.file.value = '';                                      // allow re-choosing the same file
    handleFile(f);
  });
  // quiet drag-and-drop on the easel (desktop nicety — no dropzone cliché)
  ['dragover', 'dragenter'].forEach((t) => el.easel.addEventListener(t, (e) => {
    if (step !== 'photo') return;
    e.preventDefault();
    el.easel.classList.add('dragover');
  }));
  ['dragleave', 'drop'].forEach((t) => el.easel.addEventListener(t, (e) => {
    e.preventDefault();
    el.easel.classList.remove('dragover');
  }));
  el.easel.addEventListener('drop', (e) => {
    if (step !== 'photo') return;
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFile(f);
  });

  el.orientP.addEventListener('click', () => setOrientation('portrait'));
  el.orientL.addEventListener('click', () => setOrientation('landscape'));
  el.consent.addEventListener('change', render);
  el.btnUse.addEventListener('click', useThisPhoto);
  el.btnToStyles.addEventListener('click', () => goStep('style'));
  el.btnNewPhoto.addEventListener('click', newPhoto);

  el.btnLove.addEventListener('click', loveIt);
  el.btnAnother.addEventListener('click', () => goStep('style'));
  el.btnRetry.addEventListener('click', retrySameStyle);
  el.btnDifferent.addEventListener('click', () => goStep('style'));
  el.btnKeepWaiting.addEventListener('click', keepWaiting);
  el.btnOtherStyle.addEventListener('click', () => goStep('style'));

  el.btnAdd.addEventListener('click', addToCart);
  el.btnBackPreview.addEventListener('click', () => goStep('preview'));
  el.btnAgain.addEventListener('click', () => {
    el.confirm.hidden = true;
    el.sizeui.hidden = false;
    goStep('style');
  });

  el.rail.querySelectorAll('button').forEach((b) =>
    b.addEventListener('click', () => goStep(b.dataset.step)));

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    if (!vol.img || step !== 'photo') return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(relayoutCrop, 80);
  });

  /* ---------- 13 · boot ---------- */
  buildStyleCards();
  load();
  // resume any generation that was mid-flight before a refresh (fresh 90s window)
  state.previews.forEach((p) => { if (p.status === 'painting') { p.stalled = false; startPoll(p); } });
  let s0 = (location.hash || '').replace('#', '');
  if (!STEPS.includes(s0) || !reachable(s0)) {
    const p = cur();
    s0 = p ? 'preview' : (state.uploadId ? 'style' : 'photo');
  }
  goStep(s0, true);
})();
